// Shared materials, updated in place when the theme changes (no rebuild, no shader recompile).
import {
  AddEquation, Color, CustomBlending, DoubleSide, MeshBasicMaterial, MeshStandardMaterial, OneFactor,
  ShaderMaterial, ShadowMaterial, SpriteMaterial, FrontSide, SrcAlphaFactor, ZeroFactor,
} from 'three';
import {
  blobTexture, aoRectTexture, haloTexture, tileTexture, plankTexture, rugTexture, fadeTexture,
  alphaFadeTexture, bubbleTexture, glyphAtlas,
} from './textures.js';

const SCREEN_VERT = /* glsl */`
attribute float aOn;
attribute float aPhase;
attribute vec3 aTint;
varying vec2 vUv;
varying float vOn;
varying float vPhase;
varying vec3 vTint;
void main() {
  vUv = uv;
  vOn = aOn;
  vPhase = aPhase;
  vTint = aTint;
  vec4 p = vec4(position, 1.0);
  #ifdef USE_INSTANCING
    p = instanceMatrix * p;
  #endif
  gl_Position = projectionMatrix * modelViewMatrix * p;
}
`;

const SCREEN_FRAG = /* glsl */`
uniform float uTime;
uniform vec3 uOff;
uniform vec3 uBg;
uniform vec3 uInk;
uniform float uIdle;
varying vec2 vUv;
varying float vOn;
varying float vPhase;
varying vec3 vTint;
float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
void main() {
  vec2 uv = vUv;
  float glare = (1.0 - smoothstep(0.0, 0.28, abs(uv.x * 0.8 + uv.y - 1.05))) * 0.07;
  vec3 off = uOff + vec3(glare);
  float rows = 6.0;
  float y = (1.0 - uv.y) * rows + uTime * 1.1 + vPhase * 37.0;
  float row = floor(y);
  float fy = fract(y);
  float indent = floor(hash(vec2(row, vPhase)) * 3.0) * 0.09;
  float len = 0.22 + hash(vec2(row + 7.0, vPhase)) * 0.55;
  float x = uv.x - 0.08 - indent;
  float line = step(0.0, x) * step(x, len) * smoothstep(0.18, 0.3, fy) * (1.0 - smoothstep(0.58, 0.7, fy));
  line *= step(0.14, fract(x * 5.0 + hash(vec2(row, 3.0))));
  vec3 lineCol = mix(uInk, vTint, step(0.55, hash(vec2(row, 11.0 + vPhase))));
  vec3 bg = uBg * (0.85 + 0.3 * uv.y);
  vec3 on = mix(bg, lineCol, line);
  float level = max(vOn, uIdle);
  vec3 idle = mix(off, bg * 1.6, 0.5);
  vec3 col = mix(off, mix(idle, on, clamp(vOn, 0.0, 1.0)), clamp(level, 0.0, 1.0));
  gl_FragColor = vec4(col, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

const GLYPH_VERT = /* glsl */`
attribute float aLife;
attribute float aGlyph;
attribute vec3 aColor;
attribute float aSize;
uniform float uScale;
varying float vLife;
varying float vGlyph;
varying vec3 vColor;
void main() {
  vLife = aLife;
  vGlyph = aGlyph;
  vColor = aColor;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;
  gl_PointSize = aSize * uScale;
}
`;

const GLYPH_FRAG = /* glsl */`
uniform sampler2D uAtlas;
varying float vLife;
varying float vGlyph;
varying vec3 vColor;
void main() {
  float g = floor(vGlyph + 0.5);
  vec2 cell = vec2(mod(g, 4.0), floor(g / 4.0));
  vec2 uv = (cell + gl_PointCoord) / 4.0;
  float a = texture2D(uAtlas, uv).a;
  float fade = smoothstep(0.0, 0.18, vLife) * (1.0 - smoothstep(0.65, 1.0, vLife));
  if (a * fade < 0.02) discard;
  gl_FragColor = vec4(vColor, a * fade);
  #include <colorspace_fragment>
}
`;

/**
 * Additive light that never writes alpha: the canvas is transparent (the page background shows
 * through), so a glow must add colour without making the background opaque (dark halos).
 * @template {import('three').Material} M
 * @param {M} material
 * @returns {M}
 */
export function additive(material) {
  material.blending = CustomBlending;
  material.blendEquation = AddEquation;
  material.blendSrc = SrcAlphaFactor;
  material.blendDst = OneFactor;
  material.blendEquationAlpha = AddEquation;
  material.blendSrcAlpha = ZeroFactor;
  material.blendDstAlpha = OneFactor;
  material.transparent = true;
  material.depthWrite = false;
  return material;
}

/**
 * Creates the shared material library.
 * @param {object} tokens theme tokens (palette.js THEMES.*)
 */
export function createMaterials(tokens) {
  const tx = {
    blob: blobTexture(), aoRect: aoRectTexture(), halo: haloTexture(), tile: tileTexture(),
    plank: plankTexture(), rug: rugTexture(), fade: alphaFadeTexture(), rideFade: alphaFadeTexture({ both: true }),
    beamFade: fadeTexture(), glyphs: glyphAtlas(),
    bubbles: { waiting: bubbleTexture('waiting'), error: bubbleTexture('error'), done: bubbleTexture('done') },
  };
  const std = (o) => new MeshStandardMaterial({ roughness: 0.85, metalness: 0, ...o });
  const m = {
    tx,
    tint: std({ color: 0xffffff, roughness: 0.72 }),
    body: std({ color: 0xffffff, roughness: 0.68 }),
    // The chicken (geometries.js): its colours are per vertex, the theme tints them.
    chicken: std({ color: 0xffffff, roughness: 0.74, vertexColors: true }),
    // The cat (geometries.js): grey and white per vertex as well, its fur a little more matte.
    cat: std({ color: 0xffffff, roughness: 0.82, vertexColors: true }),
    wood: std({ roughness: 0.62 }),
    deskLeg: std({ roughness: 0.5 }),
    chair: std({ roughness: 0.55 }),
    monitor: std({ roughness: 0.35 }),
    keyboard: std({ roughness: 0.6 }),
    pot: std({ roughness: 0.8 }),
    leaves: std({ color: 0xffffff, roughness: 0.8, flatShading: true }),
    lampMetal: std({ roughness: 0.4, metalness: 0.2 }),
    lampShade: std({ roughness: 0.7, side: DoubleSide, emissive: new Color(0x000000) }),
    lampBulb: new MeshBasicMaterial({ color: 0xffffff }),
    shelf: std({ roughness: 0.66 }),
    hall: std({ roughness: 0.9 }),
    walkway: std({ roughness: 0.9, map: tx.plank }),
    slab: std({ roughness: 0.92, transparent: true, opacity: 0 }),
    pillar: std({ roughness: 0.8, transparent: true, opacity: 0 }),
    star: std({ color: 0xE9A23B, roughness: 0.35, metalness: 0.25, emissive: new Color(0xE9A23B), emissiveIntensity: 0.25 }),
    glass: std({
      transparent: true, roughness: 0.1, metalness: 0, side: DoubleSide, depthWrite: false,
      alphaMap: tx.fade, emissive: new Color(0x000000),
    }),
    glassFrame: std({ roughness: 0.4, metalness: 0.3, transparent: true, alphaMap: tx.fade }),
    frame: std({ roughness: 0.45, metalness: 0.25 }),
    cabin: std({ roughness: 0.5, emissive: new Color(0x000000) }),
    cabinDoor: std({ roughness: 0.35, metalness: 0.35 }),
    ground: new ShadowMaterial({ transparent: true, depthWrite: false }),
    contact: new MeshBasicMaterial({ map: tx.blob, transparent: true, depthWrite: false, toneMapped: false }),
    podShadow: new MeshBasicMaterial({ map: tx.aoRect, transparent: true, depthWrite: false, toneMapped: false }),
    tiles: new MeshBasicMaterial({ map: tx.tile, transparent: true, depthWrite: false, opacity: 0.08 }),
    glow: additive(new MeshBasicMaterial({ map: tx.halo, toneMapped: false })),
    lampHalo: additive(new SpriteMaterial({ map: tx.halo, toneMapped: false })),
    cabinHalo: additive(new SpriteMaterial({ map: tx.halo, toneMapped: false })),
    screen: new ShaderMaterial({
      vertexShader: SCREEN_VERT, fragmentShader: SCREEN_FRAG, side: FrontSide,
      uniforms: {
        uTime: { value: 0 }, uOff: { value: new Color() }, uBg: { value: new Color() },
        uInk: { value: new Color() }, uIdle: { value: 0 },
      },
    }),
    glyphs: new ShaderMaterial({
      vertexShader: GLYPH_VERT, fragmentShader: GLYPH_FRAG, transparent: true, depthWrite: false,
      depthTest: false,
      uniforms: { uAtlas: { value: tx.glyphs }, uScale: { value: 30 } },
    }),
    bubbles: {
      waiting: new SpriteMaterial({ map: tx.bubbles.waiting, depthTest: false, depthWrite: false, transparent: true }),
      error: new SpriteMaterial({ map: tx.bubbles.error, depthTest: false, depthWrite: false, transparent: true }),
      done: new SpriteMaterial({ map: tx.bubbles.done, depthTest: false, depthWrite: false, transparent: true }),
    },
    tokens,
  };
  applyTheme(m, tokens);
  return m;
}

/**
 * Updates every shared material for a theme.
 * @param {ReturnType<typeof createMaterials>} m
 * @param {object} t theme tokens
 */
export function applyTheme(m, t) {
  m.tokens = t;
  m.wood.color.set(t.wood);
  m.deskLeg.color.set(t.deskLeg);
  m.chair.color.set(t.chair);
  m.monitor.color.set(t.monitor);
  m.keyboard.color.set(t.keyboard);
  m.pot.color.set(t.pot);
  m.lampMetal.color.set(t.lampMetal);
  m.lampShade.color.set(t.lampShade);
  m.lampShade.emissive.set(t.lampGlow);
  m.lampShade.emissiveIntensity = t.lampGlowStrength * 0.9;
  m.lampBulb.color.set(t.lampGlowStrength > 0 ? t.lampGlow : t.lampShade);
  m.shelf.color.set(t.shelf);
  m.hall.color.set(t.hall);
  m.walkway.color.set(t.walkway);
  m.slab.color.set(t.slab);
  m.pillar.color.set(t.pillar);
  m.glass.color.set(t.glass);
  m.glass.opacity = t.glassOpacity;
  m.glass.emissive.set(t.glass);
  m.glass.emissiveIntensity = t.name === 'dark' ? 0.35 : 0;
  m.glassFrame.color.set(t.glassFrame);
  m.frame.color.set(t.glassFrame);
  m.cabin.color.set(t.cabin);
  m.cabin.emissive.set(t.cabinLight);
  m.cabin.emissiveIntensity = t.name === 'dark' ? 0.55 : 0.12;
  m.cabinDoor.color.set(t.name === 'dark' ? '#3A4254' : '#CFC7B8');
  m.ground.color.set(t.shadow);
  m.ground.opacity = t.shadowOpacity;
  m.contact.color.set(t.contact);
  m.contact.opacity = t.contactOpacity;
  m.podShadow.color.set(t.contact);
  m.podShadow.opacity = t.contactOpacity * 1.2;
  m.tiles.color.set(t.name === 'dark' ? '#ffffff' : '#6b5a45');
  m.tiles.opacity = t.name === 'dark' ? 0.035 : 0.06;
  m.lampHalo.color.set(t.lampGlow);
  m.lampHalo.opacity = t.haloStrength * 0.55;
  m.cabinHalo.color.set(t.cabinLight);
  m.cabinHalo.opacity = t.haloStrength * 0.5;
  m.star.emissiveIntensity = t.name === 'dark' ? 0.6 : 0.22;
  m.chicken.color.set(t.chickenTint);
  m.cat.color.set(t.catTint);
  const u = m.screen.uniforms;
  u.uOff.value.set(t.screenOff);
  u.uBg.value.set(t.screenBg);
  u.uInk.value.set(t.screenInk);
  u.uIdle.value = t.screenIdle;
}

/** Disposes the library (materials and textures). */
export function disposeMaterials(m) {
  for (const v of Object.values(m)) {
    if (v && v.isMaterial) v.dispose();
  }
  for (const b of Object.values(m.bubbles)) b.dispose();
  for (const [k, v] of Object.entries(m.tx)) {
    if (k === 'bubbles') for (const b of Object.values(v)) b.dispose();
    else v.dispose();
  }
}
